/**
 * Stats + backups + halloween + discord + YouTube Music + git-sync hooks.
 * Grouped by feature but all sharing a single file to avoid hook-sprawl.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { StatsPeriod } from "@memes/shared";
import {
  backupsApi,
  discordApi,
  gitSyncApi,
  halloweenApi,
  lastPlayedApi,
  youtubeMusicApi,
  statsApi,
} from "../lib/api";
import { usePlayer } from "../state/player";

export function useStats(period: StatsPeriod) {
  return useQuery({
    queryKey: ["stats", period],
    queryFn: () => statsApi.period(period),
  });
}

export function useReportTime() {
  return useMutation({ mutationFn: (t: number) => statsApi.reportTime(t) });
}

export function useEndSession() {
  return useMutation({ mutationFn: (s?: number) => statsApi.endSession(s) });
}

export function useClearStats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => statsApi.clear(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stats"] }),
  });
}

/* Halloween */
export function useHalloween() {
  return useQuery({
    queryKey: ["halloween"],
    queryFn: () => halloweenApi.get(),
  });
}

export function useSetHalloween() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => halloweenApi.set(enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["halloween"] }),
  });
}

export function useMiscTracks() {
  return useQuery({
    queryKey: ["misc-tracks"],
    queryFn: () => halloweenApi.misc().then((r) => r.files),
  });
}

/* Backups */
export function useBackups() {
  return useQuery({
    queryKey: ["backups"],
    queryFn: () => backupsApi.list(),
  });
}

export function useCreateBackup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => backupsApi.create(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["backups"] }),
  });
}

export function useRestoreBackup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filename: string) => backupsApi.restore(filename),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["backups"] });
      qc.invalidateQueries({ queryKey: ["tracks"] });
    },
  });
}

/* LastPlayed */
export function useLastPlayedStorage() {
  return useQuery({
    queryKey: ["last-played"],
    queryFn: () => lastPlayedApi.get(),
  });
}

export function useClearLastPlayed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => lastPlayedApi.clear(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["last-played"] }),
  });
}

/* Git sync */
export function useGitSyncStatus() {
  return useQuery({
    queryKey: ["git-sync"],
    queryFn: () => gitSyncApi.status(),
    refetchInterval: 5_000,
  });
}

export function useTriggerGitSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => gitSyncApi.trigger(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["git-sync"] }),
  });
}

export function useForceGitRestore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => gitSyncApi.restore(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tracks"] });
      qc.invalidateQueries({ queryKey: ["git-sync"] });
    },
  });
}

export function useForceGitPush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => gitSyncApi.forcePush(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["git-sync"] }),
  });
}

/** @deprecated Prefer useForceGitRestore */
export function useForceResync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => gitSyncApi.forceResync(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tracks"] });
      qc.invalidateQueries({ queryKey: ["git-sync"] });
    },
  });
}

/* Discord */
export function useDiscordStatus() {
  return useQuery({
    queryKey: ["discord", "status"],
    queryFn: () => discordApi.status(),
    refetchInterval: 10_000,
  });
}

export function useVoiceChannels() {
  return useQuery({
    queryKey: ["discord", "voice-channels"],
    queryFn: () => discordApi.voiceChannels(),
    staleTime: 30_000,
  });
}

export function useVoiceStatus() {
  return useQuery({
    queryKey: ["discord", "voice-status"],
    queryFn: () => discordApi.voiceStatus(),
    refetchInterval: 5_000,
  });
}

export function useVoiceJoin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, guildId }: { channelId: string; guildId: string }) => {
      const { queue, index } = usePlayer.getState();
      const track = queue[index];
      return discordApi.voiceJoin(channelId, guildId, track?.id);
    },
    onSuccess: () => {
      usePlayer.getState().setVoiceConnected(true);
      void qc.invalidateQueries({ queryKey: ["discord", "voice-status"] });
    },
  });
}

export function useVoiceLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => discordApi.voiceLeave(),
    onSuccess: () => {
      usePlayer.getState().setVoiceConnected(false);
      void qc.invalidateQueries({ queryKey: ["discord", "voice-status"] });
    },
  });
}

export function useSetDiscordChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channelId: string) => discordApi.setChannel(channelId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["discord", "status"] }),
  });
}

export function useNetworkStats(enabled = true) {
  return useQuery({
    queryKey: ["discord", "network-stats"],
    queryFn: () => discordApi.networkStats(),
    enabled,
    refetchInterval: enabled ? 500 : false,
  });
}

/* YouTube Music */
export function useYoutubeMusicAvailability() {
  return useQuery({
    queryKey: ["youtube-music", "availability"],
    queryFn: () => youtubeMusicApi.availability(),
    staleTime: 60_000,
  });
}

export function useYoutubeMusicJobs() {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ["youtube-music", "jobs"],
    queryFn: async () => {
      const jobs = await youtubeMusicApi.importJobs().then((r) => r.jobs);
      if (jobs.some((j) => j.status === "completed" && j.trackIds.length > 0)) {
        void qc.invalidateQueries({ queryKey: ["tracks"] });
        void qc.invalidateQueries({ queryKey: ["owners"] });
      }
      return jobs;
    },
    refetchInterval: (q) => {
      const jobs = q.state.data;
      return jobs?.some((j) => j.status === "queued" || j.status === "running")
        ? 2_000
        : 8_000;
    },
  });
}

export function useYoutubeMusicImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ url, owner }: { url: string; owner: string }) =>
      youtubeMusicApi.importUrl({ url, owner }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["youtube-music", "jobs"] });
      qc.invalidateQueries({ queryKey: ["tracks"] });
      qc.invalidateQueries({ queryKey: ["owners"] });
    },
  });
}
